import { useTranslation } from 'react-i18next';
import Modal from '@/components/ui/Modal';
import { BLOCK_MESSAGE_KEYS, type BlockedCartLine } from './cartAvailability';

interface Props {
  open: boolean;
  blocked: BlockedCartLine[];
  /** Drops every listed line from the cart and resumes the order. */
  onRemoveAll: () => void;
  onClose: () => void;
  /** False when removing the lines would empty the cart — the primary button
   *  then only clears them instead of promising to continue to the payment. */
  canContinueAfterRemoval: boolean;
}

/**
 * The un-refreshed-page safety net. The drawer marks blocked lines in red on
 * every open, but the customer can be sitting on the page while the back-office
 * deactivates a dish — nothing re-renders. So "Passer la commande" re-checks
 * against a freshly fetched menu, and anything the check catches lands here:
 * named, with its reason, and removable in one click so the order can go through
 * instead of dying on an opaque 400 at payment.
 */
export default function UnavailableItemsModal({
  open,
  blocked,
  onRemoveAll,
  onClose,
  canContinueAfterRemoval,
}: Props) {
  const { t } = useTranslation();
  return (
    <Modal open={open} onClose={onClose} ariaLabel={t('cart.blocked.modalTitle')}>
      <div
        className="px-4 py-3 border-b-2 border-ink flex items-center justify-between"
        data-testid="cart-unavailable-modal"
      >
        <h2 className="font-display text-xl m-0">{t('cart.blocked.modalTitle')}</h2>
        <button
          type="button"
          onClick={onClose}
          className="bg-hot-pink text-[var(--on-accent)] border-2 border-ink rounded-btn font-display text-xl leading-none w-8 h-8 flex items-center justify-center"
          aria-label={t('cart.close')}
        >×</button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3">
        <p className="font-mono text-sm text-ink/80 mt-0">{t('cart.blocked.modalBody')}</p>
        <ul className="list-none pl-0 m-0 space-y-2">
          {blocked.map((entry) => (
            <li
              key={entry.lineId}
              className="border-2 border-sale-red bg-cream p-2 font-mono text-sm"
            >
              <span className="font-display">{entry.name}</span>
              <span className="block text-xs text-sale-red">
                ⚠ {t(BLOCK_MESSAGE_KEYS[entry.reason])}
              </span>
            </li>
          ))}
        </ul>
      </div>
      <div className="border-t-2 border-ink p-4 bg-cream flex flex-col gap-2">
        <button
          type="button"
          onClick={onRemoveAll}
          className="w-full bg-mint text-[var(--on-primary)] border-2 border-ink rounded-btn py-3 font-display shadow-stamp-sm"
          data-testid="cart-unavailable-remove-all"
        >
          {canContinueAfterRemoval
            ? t('cart.blocked.removeAndContinue')
            : t('cart.blocked.removeAll')}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="w-full bg-paper border-2 border-ink rounded-btn py-2 font-display text-sm"
        >
          {t('cart.blocked.keepEditing')}
        </button>
      </div>
    </Modal>
  );
}
